import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';

const useCountdown = (targetDate) => {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });

  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        expired: false,
      };
    };
    setTime(calc());
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return time;
};

const Digit = ({ value, label }) => (
  <div className="text-center">
    <span className="inline-block w-8 sm:w-12 py-0.5 sm:py-1 bg-white/10 text-white text-base sm:text-xl font-bold tabular-nums tracking-wide">
      {String(value).padStart(2, '0')}
    </span>
    <span className="block text-[8px] sm:text-[10px] tracking-[0.15em] text-white/50 mt-0.5 sm:mt-1">{label}</span>
  </div>
);

export const EventCountdownBanner = ({ event }) => {
  const t = useCountdown(event.date);

  if (t.expired) return null;

  const slug = event.slug || event.id;
  const formattedDate = new Date(event.date).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });

  return (
    <div className="bg-[#000] border-b border-[#222]" data-testid="event-countdown-banner">
      <Link
        to={`/events/${slug}`}
        className="group block sm:flex sm:items-center sm:justify-center sm:gap-6 px-4 py-2.5 sm:py-2.5 max-w-6xl mx-auto"
        data-testid="event-countdown-link"
      >
        {/* Mobile: compact two-row layout */}
        <div className="sm:hidden">
          {/* Row 1: name + location */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-white/90 text-xs font-medium truncate">{event.name}</span>
              <span className="text-white/30 text-[10px]">·</span>
              <span className="flex items-center gap-0.5 text-white/50 text-[10px] shrink-0">
                <MapPin className="w-2.5 h-2.5" />{event.location}
              </span>
            </div>
          </div>
          {/* Row 2: countdown + tap CTA */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Digit value={t.days} label="DAYS" />
              <span className="text-white/30 text-xs font-light pb-3">:</span>
              <Digit value={t.hours} label="HRS" />
              <span className="text-white/30 text-xs font-light pb-3">:</span>
              <Digit value={t.minutes} label="MIN" />
              <span className="text-white/30 text-xs font-light pb-3">:</span>
              <Digit value={t.seconds} label="SEC" />
            </div>
            <span className="flex items-center gap-1 text-[#DEA193] text-[11px] tracking-wide font-medium shrink-0 pl-3">
              Tap to view <ArrowRight className="w-3 h-3" />
            </span>
          </div>
        </div>

        {/* Desktop: single-row layout */}
        <div className="hidden sm:flex sm:items-center sm:justify-center sm:gap-6">
          <div className="flex items-center gap-2 text-white/90 text-sm tracking-wide">
            <span className="font-medium">{event.name}</span>
            <span className="text-white/30">|</span>
            <span className="flex items-center gap-1 text-white/60">
              <MapPin className="w-3 h-3" /> {event.location}
            </span>
            <span className="text-white/30">|</span>
            <span className="text-white/60">{formattedDate}</span>
          </div>

          <div className="flex items-center gap-2">
            <Digit value={t.days} label="DAYS" />
            <span className="text-white/30 text-sm font-light pb-4">:</span>
            <Digit value={t.hours} label="HRS" />
            <span className="text-white/30 text-sm font-light pb-4">:</span>
            <Digit value={t.minutes} label="MIN" />
            <span className="text-white/30 text-sm font-light pb-4">:</span>
            <Digit value={t.seconds} label="SEC" />
          </div>

          <span className="flex items-center gap-1 text-[#DEA193] text-xs tracking-wide group-hover:gap-2 transition-all">
            View Event <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>
    </div>
  );
};
